import {createReactionMenu} from '../../social/reaction-menu';
import {hasPlayableMediaSource} from '../../media/media-source';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import {PostViewModel} from '../post.view-model';
import {TimelineDataProviderService} from '../timeline.data-provider.service';
import {firstValueFrom, Observable, take} from 'rxjs';
import {ResultListApiModel} from '../../../api/models/result-list.api.model';
import {CommentApiModel} from '../../../api/models/comment.api.model';
import {KeyValuePairInterface} from '../../interfaces/key-value-pair.interface';
import {CurrentUserViewModel} from '../../../core/models/current-user.view-model';
import {MediaObjectApiModel} from '../../../api/models/media-object.api-model';
import {SocialInputSubmitInterface} from '../../social/social-input/social-input-submit.interface';
import {PostTypeEnum} from '../../../core/enums/post-type.enum';
import {EventShowTabsEnum} from '../../../features/event/event-show/event-show-tabs.enum';
import {ModalStateEvent} from '../../modal/modal-state-event';
import {ModalService} from '../../modal/modal.service';
import {MediaViewComponent} from '../../media-view/media-view.component';
import {VideoService} from '../video.service';
import {MediaTypeEnum} from '../../../core/enums/media-type.enum';
import * as _ from 'lodash';
import {MenuItem} from 'primeng/api';
import {ReactedWithEnum} from '../../../core/enums/reacted-with.enum';
import {ReactionApiModel} from '../../../api/models/reaction.api.model';
import {UserApiModel} from '../../../api/models/user.api-model';
import {SpeedDialComponent} from '../../speed-dial/speed-dial.component';
import {ReactionActionTypes} from '../../../state/reaction/reaction.actions';
import {Store} from '@ngrx/store';
import {AppState} from '../../../state/app.state';
import {ReactionTypeEnum} from '../../../core/enums/reaction-type.enum';
import {ReactionSubjectInterface} from './reaction-subject.interface';
import {TruncateOnWordPipe} from "../../../core/pipes/truncate-on-word.pipe";
import {RemoveHtmlTagsPipe} from "../../../core/pipes/remove-html-tags.pipe";
import {TranslateService} from "@ngx-translate/core";
import {AbstractComponent} from "../../../core/components/abstract-component";
import {BottomSheetComponent} from "../../components/bottom-sheet/bottom-sheet.component";
import { ViewTypeEnum } from '../../../core/enums/view-type.enum';
import { MediaObjectPipe } from '../../../core/pipes/media-object.pipe';
import { QrDialogData } from "../../qr/qr-dialog.component";
import { PublicResourceUrlService } from "../../services/public-resource-url.service";
import {ReactionListCacheFacade} from '../../modal/components/social-list/reaction-list-cache.facade';
import {ProjectViewModel} from '../../../features/user/garage/project.view-model';
import {EventApiModel} from '../../../api/models/event.api.model';
import {OverlayGalleryModalResult} from '../../modal/components/overlay-gallery/overlay-gallery-modal.interface';

export type TimelineGridTileClickAction = 'navigate-to-list' | 'open-gallery';

@Component({
  selector: 'app-timeline-tile',
  templateUrl: './timeline-tile.component.html',
  styleUrls: ['./timeline-tile.component.scss']
})
export class TimelineTileComponent extends AbstractComponent implements OnInit, OnChanges, AfterViewInit, AfterViewChecked, OnDestroy {
  get post(): PostViewModel {
    return this._post;
  }

  @Input() set post(value: PostViewModel) {
    this._post = PostViewModel.ensureInstance(value);
    if ((this._post.reactions?.length || this._post.currentUserReaction) && this.currentUser) {
      this.reactionSubject = this._post;
      this.syncIsReacted();
    }
  }

  constructor(
    private readonly timelineDPS: TimelineDataProviderService,
    private readonly cdr: ChangeDetectorRef,
    private readonly ms: ModalService,
    private readonly vs: VideoService,
    private readonly store: Store<AppState>,
    private readonly translate: TranslateService,
    private readonly publicResourceUrlService: PublicResourceUrlService,
    private readonly reactionListCacheFacade: ReactionListCacheFacade
  ) {
    super();
  }

  public activeIndex = 0;
  private _post: PostViewModel;
  @Input() currentUser: CurrentUserViewModel;
  @Input() permissions: string[] = [];
  @Input() shareTargetProject: ProjectViewModel | null = null;
  @Input() shareTargetEvent: EventApiModel | null = null;
  @Input() index: number = null;
  @Input() fetchDeleted: boolean;
  @Input() viewType: ViewTypeEnum = ViewTypeEnum.LIST;
  @Input() gridAspectRatio = 1;
  /** Static Home swipe preview: cards look normal but own no interaction state. */
  @Input() renderMode: 'active' | 'swipe-preview' = 'active';
  @Input() previewMediaEnabled = true;
  /** Parent-controlled deferral for a grid temporarily covered by a gallery. */
  @Input() deferGridMedia = false;
  @Input() gridTileClickAction: TimelineGridTileClickAction = 'navigate-to-list';
  /** Runtime scope of the Media grid's bare viewport decoder pool. */
  @Input() gridViewportDecoderScope: string | null = null;
  @Input() visibleMediaType: MediaTypeEnum | null = null;
  @Input() galleryPostSequence$?: Observable<PostViewModel[]>;
  @Input() galleryLoadNextPostPage?: () => Promise<boolean>;
  @Input() galleryHasNextPostPage?: () => boolean;
  @Input() galleryPostPageSize?: number;
  @Input() galleryPrefetchAtPostIndex?: number;
  @Input() galleryPosts: PostViewModel[] = [];
  @Output() postContextMenuOpen: EventEmitter<PostViewModel> = new EventEmitter<PostViewModel>();
  @Output() commentContextMenuOpen: EventEmitter<CommentApiModel> = new EventEmitter<CommentApiModel>();
  @Output() mobileCommentsOpen: EventEmitter<PostViewModel> = new EventEmitter<PostViewModel>();
  @Output() gridTileOpen: EventEmitter<PostViewModel> = new EventEmitter<PostViewModel>();
  @Output() gridImageAspectRatioChange = new EventEmitter<{post: PostViewModel; aspectRatio: number}>();
  /** Emits pointer intent without opening or otherwise changing the gallery. */
  @Output() gridTileVideoIntent: EventEmitter<PostViewModel> = new EventEmitter<PostViewModel>();
  @Output() sharedPostCreated: EventEmitter<PostViewModel> = new EventEmitter<PostViewModel>();
  @Output() shareCountIncremented: EventEmitter<{ postId: number; delta: number }> = new EventEmitter<{ postId: number; delta: number }>();
  public embed: any;
  public isTextExpanded = false;
  public isCommentBoxOpened = false;
  public isThreadListOpened = false;
  public isThreadCommentListOpened: KeyValuePairInterface<number, boolean>[] = [];
  public isThreadCommentInputOpened: KeyValuePairInterface<number, boolean>[] = [];
  public desktopReplyContextComment: CommentApiModel | null = null;
  public desktopReplyThreadId: number | null = null;
  public comments: CommentApiModel[] = [];
  public commentBoxLoaded = false;
  public skeletonRandomNumber: number;
  public readonly PostTypeEnum = PostTypeEnum;
  public readonly EventShowTabsEnum = EventShowTabsEnum;
  @ViewChild(MediaViewComponent) mediaView: MediaViewComponent;
  @ViewChild(SpeedDialComponent) speedDial: SpeedDialComponent;
  @ViewChild('shareMenu') shareMenu: any;
  @ViewChildren('gridImage') private readonly gridImages?: QueryList<ElementRef<HTMLImageElement>>;

  public readonly Array = Array;
  public items: MenuItem[];
  public shareMenuItems: MenuItem[] = [];

  public readonly ReactedWithEnum = ReactedWithEnum;
  public readonly ReactionTypeEnum = ReactionTypeEnum;
  public readonly ViewTypeEnum = ViewTypeEnum;
  public reactionType: ReactionTypeEnum;
  public reactionSubject: ReactionSubjectInterface;
  public isMobileView: boolean = false;
  public qrDialogVisible = false;
  public qrDialogData: QrDialogData | null = null;
  public readonly inProgressStatuses = new Set(['pending', 'processing', 'uploaded', 'pending_upload']);
  public loadedGridImageSrc: string | null = null;
  private gridImageReadyFrameId: number | null = null;

  get isGridView(): boolean {
    return this.viewType === ViewTypeEnum.GRID;
  }

  get isListView(): boolean {
    return this.viewType === ViewTypeEnum.LIST;
  }

  get canOpenPostContextMenu(): boolean {
    return !!this.currentUser && !!this.post?.postedBy;
  }

  public get isSwipePreview(): boolean {
    return this.renderMode === 'swipe-preview';
  }

  public get isGridMediaDeferred(): boolean {
    return this.deferGridMedia || (this.isSwipePreview && !this.previewMediaEnabled);
  }

  ngOnInit() {
    if (this.isSwipePreview) {
      this.skeletonRandomNumber = 1;
      return;
    }
    this.initializeActiveTile();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['renderMode']
      && !changes['renderMode'].firstChange
      && changes['renderMode'].previousValue === 'swipe-preview'
      && this.renderMode === 'active'
    ) {
      this.initializeActiveTile();
    }
  }

  private initializeActiveTile(): void {
    if (this.items?.length || this.shareMenuItems.length) {
      return;
    }
    this.updateViewportFlags();
    this.skeletonRandomNumber = _.random(1, 5, false);

    this.items = createReactionMenu(reactedWith => {
      this.subscriptions.push(this.timelineDPS.createReaction(reactedWith, this.reactionSubject, this.reactionType)
        .subscribe((reactionAM: ReactionApiModel) => this.syncReaction(reactionAM)));
    });

    this.shareMenuItems = [
      {
        label: this.translate.instant('shared.share.menu.feed'),
        icon: 'pi pi-megaphone',
        command: () => this.openShareComposer(),
      },
      {
        label: this.translate.instant('shared.share.menu.chat'),
        icon: 'pi pi-send',
        command: () => this.openChatShareModal(),
      },
      {
        label: this.translate.instant('shared.share.menu.external'),
        icon: 'pi pi-qrcode',
        command: () => this.openQrDialog(),
      }
    ];
  }

  ngAfterViewInit(): void {
  }

  ngAfterViewChecked(): void {
    this.scheduleCachedGridImageCheck();
  }

  ngOnDestroy() {
    this.mediaGridDestroyed = true;
    if (this.gridImageReadyFrameId !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.gridImageReadyFrameId);
      this.gridImageReadyFrameId = null;
    }
    super.ngOnDestroy();
    // A tile may temporarily outlive its parent LView through browser or
    // overlay callbacks. Do not let its gallery inputs retain the destroyed
    // TimelineComponent and its virtual viewport through bound closures.
    this.galleryPostSequence$ = undefined;
    this.galleryLoadNextPostPage = undefined;
    this.galleryHasNextPostPage = undefined;
    this.galleryPostPageSize = undefined;
    this.galleryPrefetchAtPostIndex = undefined;
    this.galleryPosts = [];
  }


  onCommentSubmit(event: SocialInputSubmitInterface, postVM: PostViewModel): void {
    this.subscriptions.push(this.timelineDPS.createComment(event.content, postVM, undefined, event.mediaObjectAMs).subscribe({
      next: (commentAM: CommentApiModel) => {
        event.input.reset();
        this.comments.unshift(commentAM);
        this.post.comments.unshift(commentAM['@id']);
        this.post.commentsCount = (this.post.commentsCount || 0) + 1;
        this.assignReplyBooleanMap(commentAM);
        this.assignThreadCommentsMap(commentAM);
      },
      error: () => event.input.restoreAfterFailedSubmit()
    }));
  }

  public hasMediaOrLocalPreview(): boolean {
    return !!this.post?.media?.length || !!this.post?.localThumbnailUri || this.shouldShowProcessingPlaceholder();
  }

  public shouldShowLocalProcessingPreview(): boolean {
    if (!this.post?.localThumbnailUri) {
      return false;
    }

    return !this.hasRenderableMedia(this.post);
  }

  public shouldShowLocalProcessingLoader(): boolean {
    if (!this.shouldShowLocalProcessingPreview()) {
      return false;
    }
    const ownerId = (this.post?.postedBy as any)?.id;
    if (!ownerId || ownerId !== this.currentUser?.id) {
      return false;
    }

    const primaryMedia = this.post?.media?.[0] as any;
    if (primaryMedia?.type === MediaTypeEnum.VIDEO) {
      return !this.hasRenderableVideoSource(primaryMedia);
    }

    return this.inProgressStatuses.has((this.post?.processingStatus || '').toString());
  }

  public shouldShowProcessingPlaceholder(): boolean {
    if (!this.post || this.post.isPlaceholder) {
      return false;
    }

    if (this.post.localThumbnailUri) {
      return false;
    }

    const ownerId = (this.post?.postedBy as any)?.id;
    if (!ownerId || ownerId !== this.currentUser?.id) {
      return false;
    }

    if (!this.post?.media?.length) {
      return false;
    }

    if (this.hasRenderableMedia(this.post)) {
      return false;
    }

    const primaryMedia = this.post?.media?.[0] as any;
    if (primaryMedia?.type === MediaTypeEnum.VIDEO) {
      return !this.hasRenderableVideoSource(primaryMedia);
    }

    return true;
  }

  public onGridTileClick(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isSwipePreview) {
      return;
    }
    if (!this.post || this.post.isPlaceholder || !this.post.id) {
      return;
    }
    if (this.gridTileClickAction === 'open-gallery') {
      this.emitGridTileVideoIntent();
      void this.openGalleria(event || new Event('click'), 0);
      return;
    }
    this.gridTileOpen.emit(this.post);
  }

  public onGridTileIntent(): void {
    this.emitGridTileVideoIntent();
  }

  private emitGridTileVideoIntent(): void {
    if (
      this.isSwipePreview
      || this.gridTileClickAction !== 'open-gallery'
      || !this.post
      || this.post.isPlaceholder
      || !this.post.id
      || this.getDisplayMediaAt(0)?.type !== MediaTypeEnum.VIDEO
    ) {
      return;
    }
    this.gridTileVideoIntent.emit(this.post);
  }

  public getGridThumbnailSource(): 'local' | 'backend' | 'none' {
    if (this.hasBackendGridThumbnail()) {
      return 'backend';
    }
    if (this.post?.localThumbnailUri) {
      return 'local';
    }
    return 'none';
  }

  public getLocalPreviewMediaObject(): MediaObjectApiModel | null {
    if (!this.post?.localThumbnailUri) {
      return null;
    }

    return {
      id: this.post?.id || -1,
      type: MediaTypeEnum.IMAGE as any,
      hyperlinks: [
        { name: 'thumb_md', uri: this.post.localThumbnailUri },
        { name: 'thumb_sm', uri: this.post.localThumbnailUri },
        { name: 'webp', uri: this.post.localThumbnailUri },
        { name: 'original', uri: this.post.localThumbnailUri },
      ] as any,
    } as MediaObjectApiModel;
  }

  public getGridThumbnail(): string | null {
    if (!this.post) {
      return null;
    }
    const media = this.getDisplayMediaAt(0) || (!this.visibleMediaType ? this.post.media?.[0] : null);
    if (!media) {
      return this.post.localThumbnailUri || null;
    }
    const formatType = media.type === MediaTypeEnum.VIDEO ? 'thumbnail' : 'thumb_md';
    const uri = MediaObjectPipe.transformToLink(media, { formatType, defaultImageType: 'background' });
    if (uri && !uri.startsWith('assets/')) {
      return uri;
    }

    return this.post.localThumbnailUri || null;
  }

  public onGridImageError(): void {
    if (!this.post?.localThumbnailUri?.startsWith('blob:')) {
      return;
    }

    this.post.localThumbnailUri = null;
    this.cdr.markForCheck();
  }

  public onGridImageLoaded(src: string | null | undefined, image?: HTMLImageElement): void {
    this.loadedGridImageSrc = src || null;
    if (this.shareTargetEvent && image?.naturalWidth > 0 && image?.naturalHeight > 0) {
      this.gridImageAspectRatioChange.emit({
        post: this.post,
        aspectRatio: image.naturalWidth / image.naturalHeight,
      });
    }
    this.cdr.markForCheck();
  }

  public isGridImageLoaded(src: string | null | undefined): boolean {
    return !!src && this.loadedGridImageSrc === src;
  }

  /**
   * The closest swipe preview is already a bounded viewport-sized DOM tree.
   * It must begin its native image requests while it is still offscreen,
   * otherwise browser lazy loading defers the thumbnails until the handoff.
   */
  public getGridImageLoading(): 'eager' | null {
    return this.isSwipePreview && this.previewMediaEnabled && !this.isGridMediaDeferred
      ? 'eager'
      : null;
  }

  public resolveGridImageSource(src: string | null | undefined): string {
    return src || '';
  }

  private scheduleCachedGridImageCheck(): void {
    if (this.gridImageReadyFrameId !== null || !this.gridImages?.length || typeof requestAnimationFrame !== 'function') {
      return;
    }

    this.gridImageReadyFrameId = requestAnimationFrame(() => {
      this.gridImageReadyFrameId = null;
      this.markCachedGridImagesAsLoaded();
    });
  }

  private markCachedGridImagesAsLoaded(): void {
    this.gridImages?.forEach(({nativeElement: image}) => {
      const src = image.dataset.gridImageSrc;
      if (src && image.complete && image.naturalWidth > 0 && !this.isGridImageLoaded(src)) {
        this.onGridImageLoaded(src, image);
      }
    });
  }

  private hasBackendGridThumbnail(): boolean {
    const media = this.getDisplayMediaAt(0);
    if (!media) {
      return false;
    }

    const formatType = media.type === MediaTypeEnum.VIDEO ? 'thumbnail' : 'thumb_md';
    const uri = MediaObjectPipe.transformToLink(media, { formatType, defaultImageType: 'background' });
    return !!uri && !uri.startsWith('assets/');
  }

  public getDisplayMediaCount(): number {
    return this.getDisplayMedia().length;
  }

  public getDisplayMediaAt(index: number): MediaObjectApiModel | null {
    return this.getDisplayMedia()[index] || null;
  }

  public getDisplayMediaSlice(start: number, end?: number): MediaObjectApiModel[] {
    return this.getDisplayMedia().slice(start, end);
  }

  private getDisplayMedia(post: PostViewModel = this.post): MediaObjectApiModel[] {
    const media = post?.media || [];
    return this.visibleMediaType ? media.filter((item) => item?.type === this.visibleMediaType) : media;
  }

  public hasDisplayVideoMedia(): boolean {
    return this.getDisplayMedia().some((media) => media?.type === MediaTypeEnum.VIDEO);
  }

  public openDisplayMedia(event: Event, index: number, object?: MediaViewComponent): void {
    if (index >= this.getDisplayMediaCount()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    void this.openGalleria(event, index, object);
  }

  onReplySubmit(event: SocialInputSubmitInterface, postVM: PostViewModel, commentAM: CommentApiModel): void {
    this.subscriptions.push(this.timelineDPS.createComment(event.content, postVM, commentAM, event.mediaObjectAMs).subscribe({
      next: (replyAM: CommentApiModel) => {
        event.input.reset();
        commentAM.responses.push(replyAM);
        this.desktopReplyContextComment = null;
        this.desktopReplyThreadId = null;
      },
      error: () => event.input.restoreAfterFailedSubmit()
    }));
  }

  onCommentBoxOpen(postVM: PostViewModel): void {
    if (this.isSwipePreview) {
      return;
    }
    if (this.isMobileView) {
      this.mobileCommentsOpen.emit(postVM);
      return;
    }

    if (!this.isCommentBoxOpened) {
      this.commentBoxLoaded = false;
      this.subscriptions.push(this.timelineDPS.getCommentCollection(postVM, this.fetchDeleted)
        .subscribe((resultListAM: ResultListApiModel<CommentApiModel>) => {
          this.comments = resultListAM.records;
          this.comments.forEach((thread: CommentApiModel) => {
            thread.currentUserReaction = thread.reactions.find((reaction: ReactionApiModel) => reaction.author['@id'] === this.currentUser['@id']);
            thread.responses.forEach((response: CommentApiModel) => response.currentUserReaction = response.reactions.find((reaction: ReactionApiModel) => reaction.author['@id'] === this.currentUser['@id']));
          });
          this.assignReplyBooleanMap();
          this.assignThreadCommentsMap();
          this.commentBoxLoaded = true;
          this.cdr.detectChanges();
          // this.timeline.ig.updateItems();
        }));
    }
    this.isCommentBoxOpened = !this.isCommentBoxOpened;
    this.cdr.detectChanges();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportFlags();
  }

  toggleReplyBooleanMap(commentAM: CommentApiModel, contextComment: CommentApiModel = commentAM): void {
    this.toggleThreadCommentsMap(commentAM);
    const openedEntry = this.isThreadCommentInputOpened.find(e => e.key === commentAM.id);
    const isSameContextClicked = openedEntry?.value
      && this.desktopReplyThreadId === commentAM.id
      && this.desktopReplyContextComment?.id === contextComment.id;

    this.comments.forEach((c: CommentApiModel) => {
      const index = this.isThreadCommentInputOpened.findIndex(e => e.key === c.id);
      this.isThreadCommentInputOpened[index].value = !isSameContextClicked && commentAM.id === c.id;
    });

    if (isSameContextClicked) {
      this.desktopReplyContextComment = null;
      this.desktopReplyThreadId = null;
      return;
    }

    this.desktopReplyContextComment = contextComment;
    this.desktopReplyThreadId = commentAM.id;
    // this.timeline.ig.updateItems();
  }

  public getReplyInputLabel(comment: CommentApiModel): string {
    const username = this.desktopReplyThreadId === comment?.id
      ? this.desktopReplyContextComment?.author?.username || comment?.author?.username
      : comment?.author?.username;

    if (username) {
      return `${this.translate.instant('common.reply')} @${username}`;
    }

    return this.translate.instant('common.write_something');
  }

  assignReplyBooleanMap(commentAM?: CommentApiModel): void {
    if (commentAM) {
      this.isThreadCommentInputOpened.push(Object.assign({key: commentAM.id, value: false}));
      return;
    }

    this.comments.forEach((commentAM: CommentApiModel) => {
      this.isThreadCommentInputOpened.push(Object.assign({key: commentAM.id, value: false}));
    });
  }

  toggleThreadCommentsMap(commentAM: CommentApiModel): void {
    commentAM.responses = commentAM.responses.reverse();
    const index = this.isThreadCommentListOpened.findIndex(e => e.key === commentAM.id);
    this.isThreadCommentListOpened[index].value = !this.isThreadCommentListOpened[index].value;
    // this.timeline.ig.updateItems();
  }

  assignThreadCommentsMap(commentAM?: CommentApiModel): void {
    if (commentAM) {
      this.isThreadCommentListOpened.push(Object.assign({key: commentAM.id, value: false}));
      return;
    }

    this.comments.forEach((commentAM: CommentApiModel) => {
      this.isThreadCommentListOpened.push(Object.assign({key: commentAM.id, value: false}));
    });
  }

  isItemOpened(map: KeyValuePairInterface<number, boolean>[], comment: CommentApiModel): boolean {
    return map.find(e => e.key === comment.id).value;
  }

  public emitPostContextMenuOpen(post: PostViewModel): void {
    if (this.isSwipePreview) {
      return;
    }
    this.postContextMenuOpen.emit(post);
  }

  public emitCommentContextMenuOpen(commentAM: CommentApiModel): void {
    if (this.isSwipePreview) {
      return;
    }
    this.commentContextMenuOpen.emit(commentAM);
  }

  public removeComment(commentAM: CommentApiModel): void {
    if (commentAM.thread) {
      const thread = this.comments.find( e => e['@id'] === commentAM.thread);
      thread.responses = thread.responses.filter(e => e['@id'] !== commentAM['@id']);
      return;
    }
    this.comments = this.comments.filter(e => e['@id'] !== commentAM['@id']);
    this.post.comments = this.post.comments.filter(e => e !== commentAM['@id']);
    this.post.commentsCount = Math.max(0, (this.post.commentsCount || 0) - 1);
  }

  public upsertComment(commentAM: CommentApiModel, includeOwn = false): void {
    if (!includeOwn && commentAM.author?.slug === this.currentUser?.slug) {
      return;
    }

    if (commentAM.thread) {
      const thread = this.comments.find( e => e['@id'] === commentAM.thread);
      const reply = thread.responses.find( e => e['@id'] === commentAM['@id']);
      if (reply) {
        reply.content = commentAM.content;
        reply.media = commentAM.media;
        return;
      }
      thread.responses.push(commentAM);
      return;
    }

    const comment = this.comments.find(e => e['@id'] === commentAM['@id']);
    if (comment) {
      comment.content = commentAM.content;
      comment.media = commentAM.media;
      return;
    }

    this.assignReplyBooleanMap(commentAM);
    this.assignThreadCommentsMap(commentAM);
    this.comments.unshift(commentAM);
    this.post.comments.unshift(commentAM['@id']);
    this.post.commentsCount = (this.post.commentsCount || 0) + 1;
    this.cdr.detectChanges();
  }

  public imageContainerStyles(mediaObjectAM: MediaObjectApiModel, count: number): object {
    return {backgroundColor: 'black', backgroundSize: count > 1 ? 'cover' : 'contain',  backgroundPosition: 'center', backgroundRepeat: 'no-repeat', maxHeight: count > 1 && mediaObjectAM.type !== MediaTypeEnum.VIDEO  ? false : '960px'};
  }

  public getHiddenMediaCount(mediaCount: number): number {
    return Math.max(0, mediaCount - 5);
  }

  public hasVideoMedia(): boolean {
    return !!this.post?.media?.some((media) => media?.type === MediaTypeEnum.VIDEO);
  }

  public getLoadingMediaRatio(count: number, index: number): number {
    if (count <= 1) {
      return 1;
    }

    return 9 / 16;
  }

  private mediaGridDestroyed = false;

  async openGalleria(event: Event, activeIndex: number, object?: MediaViewComponent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    if (BottomSheetComponent.hasAnyPresented()) {
      return;
    }

    if (this.mediaGridDestroyed) {
      return;
    }
    const selectedPostId = this.post?.id;
    const selectedMediaId = this.getDisplayMediaAt(activeIndex)?.id;
    if (!selectedMediaId) {
      return;
    }
    if (!hasPlayableMediaSource(this.getDisplayMediaAt(activeIndex))) {
      await this.refreshPostMedia();
      if (this.mediaGridDestroyed || this.post?.id !== selectedPostId) {
        return;
      }
      activeIndex = this.getDisplayMedia(this.post).findIndex((media) => media.id === selectedMediaId);
      if (activeIndex < 0) {
        return;
      }
    }

    this.activeIndex = activeIndex;
    const hasControllableSourcePlayer = !!object?.hasControllablePlayer();
    const isSourcePlaybackPaused = hasControllableSourcePlayer ? object.isPlaybackPaused() : true;
    if (hasControllableSourcePlayer && isSourcePlaybackPaused) {
      this.vs.pauseAllExcept(object.id);
      object.togglePlay({event});
      return;
    }
    // this.displayImgFullscreen = true;
    const selectedMedia = this.gridTileClickAction === 'open-gallery'
      ? this.getDisplayMediaAt(activeIndex)
      : null;
    // Transfer only the selected decoder. Gallery neighbours are disk-warmed
    // after the first frame, so opening a modal cannot start three concurrent
    // HLS/MSE pipelines on a mobile device.
    const viewportDecoderHandoffs = selectedMedia?.type === MediaTypeEnum.VIDEO
      ? this.vs.claimViewportWarmDecoders(this.gridViewportDecoderScope, [selectedMedia.id])
      : [];
    this.subscriptions.push(this.ms.showOverlayGalleryModal({
      post: this.post,
      posts: this.getGalleryPosts(),
      postIndex: this.getGalleryPostIndex(),
      currentUser: this.currentUser,
      component: object,
      activeIndex,
      initialCurrentTime: object?.getPlaybackCurrentTime() ?? 0,
      initialWasPlaying: hasControllableSourcePlayer && !isSourcePlaybackPaused,
      sourceVideoKey: hasControllableSourcePlayer ? object?.videoKey ?? null : null,
      visibleMediaType: this.visibleMediaType,
      postSequence$: this.galleryPostSequence$,
      loadNextPostPage: this.galleryLoadNextPostPage,
      hasNextPostPage: this.galleryHasNextPostPage,
      postPageSize: this.galleryPostPageSize,
      prefetchAtPostIndex: this.galleryPrefetchAtPostIndex,
      viewportDecoderHandoffs,
      viewportDecoderScope: viewportDecoderHandoffs.length ? this.gridViewportDecoderScope : null,
    }).successState.pipe(take(1)).subscribe((modalStateEvent: ModalStateEvent) => {
      const result = modalStateEvent.data as OverlayGalleryModalResult;
      if (result.component?.hasControllablePlayer() && result.sourceVideoKey) {
        result.component.resumePlaybackFrom(result.currentTime, this.vs.muted);
      }
      // this.historyPostVM = modalStateEvent.data;
      // this.historyPostVMs.unshift(this.historyPostVM);
    }));
  }

  private getGalleryPosts(): PostViewModel[] {
    const posts = (this.galleryPosts || [])
      .filter((post) => !post?.isPlaceholder && !!post?.id && !!post?.media?.length)
      .map((post) => post.id === this.post?.id ? this.post : post);
    if (this.post && !posts.some((post) => post.id === this.post.id)) {
      posts.push(this.post);
    }
    return posts;
  }

  private getGalleryPostIndex(): number {
    const index = this.getGalleryPosts().findIndex((post) => post.id === this.post?.id);
    return Math.max(0, index);
  }

  private async refreshPostMedia(): Promise<void> {
    if (!this.post?.id) {
      return;
    }
    try {
      const requestedPostId = this.post.id;
      const latest = await firstValueFrom(this.timelineDPS.getPost(String(requestedPostId), this.fetchDeleted));
      if (!latest || this.mediaGridDestroyed || this.post?.id !== requestedPostId) {
        return;
      }
      this.post = latest;
      this.cdr.detectChanges();
    } catch {
      // noop
    }
  }

  private hasRenderableMedia(post: PostViewModel | null | undefined): boolean {
    return this.getDisplayMedia(post).some(hasPlayableMediaSource);
  }

  private hasPlayablePrimaryVideo(): boolean {
    const primaryMedia = this.post?.media?.[0] as any;
    if (!primaryMedia || primaryMedia.type !== MediaTypeEnum.VIDEO) {
      return false;
    }

    return this.hasRenderableVideoSource(primaryMedia);
  }

  private hasRenderableVideoSource(media: MediaObjectApiModel | null | undefined): boolean {
    return media?.type === MediaTypeEnum.VIDEO && hasPlayableMediaSource(media);
  }

  private updateViewportFlags(): void {
    this.isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;
  }

  toggleThreadList(): void {
    this.isThreadListOpened = !this.isThreadListOpened;
    // this.timeline.ig.updateItems();
  }

  public toggleReactionBtn(e, subject: ReactionSubjectInterface, type: ReactionTypeEnum, speedDial: SpeedDialComponent) {
    e.preventDefault();
    if (this.isSwipePreview) {
      return;
    }
    this.reactionType = type;
    this.reactionSubject = subject;
    speedDial.onButtonClick(e);
  }

  public toggleThumbsUp(e, subject: ReactionSubjectInterface, type: ReactionTypeEnum, speedDial: SpeedDialComponent) {
    e.preventDefault();
    if (this.isSwipePreview) {
      return;
    }
    this.reactionType = type;
    this.reactionSubject = subject;

    if (speedDial.visible) {
      return;
    }

    if (subject.currentUserReaction) {
      const currentReaction = subject.currentUserReaction;
      this.subscriptions.push(this.timelineDPS.removeReaction(subject.currentUserReaction).subscribe(() => {
        this.reactionListCacheFacade.removeReactionFromTargets(currentReaction['@id'], [
          {listType: 'local-subject-reactions', subjectId: subject['@id']}
        ]);
        subject.reactions = subject.reactions.filter((x: ReactionApiModel) => (x.author as UserApiModel)['@id'] !== (currentReaction.author as UserApiModel)['@id']);
        subject.reactionsCount = Math.max(0, (subject.reactionsCount || subject.reactions.length || 0) - 1);
        subject.currentUserReaction = null;
      }));
      return;
    }

    this.subscriptions.push(this.timelineDPS.createReaction(ReactedWithEnum.THUMBS_UP, subject, this.reactionType).subscribe((reactionAM: ReactionApiModel) => {
      this.syncReaction(reactionAM);
    }));
  }


  public syncReaction(reactionAM: ReactionApiModel): void {
    const hadCurrentUserReaction = !!this.reactionSubject.currentUserReaction;
    this.reactionSubject.reactions = this.reactionSubject.reactions.filter((x: ReactionApiModel) => (x.author as UserApiModel)['@id'] !== (reactionAM.author as UserApiModel)['@id']);
    this.reactionSubject.reactions.unshift(reactionAM);
    if (!hadCurrentUserReaction) {
      this.reactionSubject.reactionsCount = (this.reactionSubject.reactionsCount || 0) + 1;
    }
    this.syncIsReacted();
    this.reactionListCacheFacade.addReactionToTargets(reactionAM, [
      {listType: 'local-subject-reactions', subjectId: this.reactionSubject['@id']}
    ]);
  }

  public syncIsReacted(): void {
    this.reactionSubject.currentUserReaction = this.reactionSubject.reactions?.find((x: ReactionApiModel) => (x.author as UserApiModel)['@id'] === this.currentUser['@id']) || this.reactionSubject.currentUserReaction || null;
  }

  public openSocialModal(e: Event, subject: ReactionSubjectInterface) {
    e.preventDefault();
    e.stopImmediatePropagation();
    if (this.isSwipePreview) {
      return;
    }
    this.reactionSubject = subject;
    this.subscriptions.push(this.ms.showReactionsModal({
      listType: 'local-subject-reactions',
      subject,
      initialData: this.reactionSubject.reactions
    }).successState.subscribe());
  }

  public openQrDialog(): void {
    const sharePost = this.resolveCurrentSharePost();
    this.qrDialogData = {
      resourceType: 'post',
      title: this.getShareTitle(sharePost),
      subtitle: TruncateOnWordPipe.truncateOnWord(RemoveHtmlTagsPipe.sanitize(sharePost.content), 255, '...'),
      url: this.getPostUrl(sharePost),
    };
    this.qrDialogVisible = true;
  }

  public openShareMenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.shareMenu?.toggle(event);
  }

  public openShareComposer(): void {
    if (!this.currentUser) {
      return;
    }

    const sharePost = this.resolveSharePayloadPost();
    this.subscriptions.push(this.ms.showPostComposerModal({
      currentUserVM: this.currentUser,
      projectVM: this.shareTargetProject,
      eventAM: this.shareTargetEvent,
      sharedPost: sharePost,
    }).successState.subscribe((modalStateEvent: ModalStateEvent) => {
      const postVM = modalStateEvent?.data as PostViewModel;
      if (!postVM) {
        return;
      }

      this.emitShareCountIncrement(sharePost.id, 1);
      this.sharedPostCreated.emit(postVM);
    }));
  }

  public openChatShareModal(): void {
    if (!this.currentUser) {
      return;
    }

    const sharePost = this.resolveSharePayloadPost();
    this.subscriptions.push(this.ms.showChatShareModal({
      currentUserVM: this.currentUser,
      sharedPost: sharePost,
    }).successState.subscribe((modalStateEvent: ModalStateEvent) => {
      const sharedMessages = Array.isArray(modalStateEvent?.data) ? modalStateEvent.data : [];
      this.emitShareCountIncrement(sharePost.id, Math.max(1, sharedMessages.length));
    }));
  }

  getPostUrl(post: PostViewModel = this.post): string {
    return this.publicResourceUrlService.getPostUrl(post.id);
  }

  private getShareTitle(post: PostViewModel = this.post): string {
    if (post.type === PostTypeEnum.PROJECT_POST || post.type === PostTypeEnum.PROJECT_MEDIA_POST) {
      return post.project?.vehicle?.fullVehicleIdentity || post.postedBy?.username || 'Post';
    }

    if (post.type === PostTypeEnum.EVENT_DISCUSSION_POST) {
      return post.event?.name || post.postedBy?.username || 'Post';
    }

    if (post.type === PostTypeEnum.COLLABORATION_MEDIA_POST) {
      return post.collaboration?.title
        || post.collaboration?.project?.vehicle?.fullVehicleIdentity
        || post.postedBy?.username
        || 'Post';
    }

    return post.postedBy?.username || 'Post';
  }

  private resolveCurrentSharePost(): PostViewModel {
    return PostViewModel.ensureInstance(this.post);
  }

  private resolveSharePayloadPost(): PostViewModel {
    const post = PostViewModel.ensureInstance(this.post);
    return post?.getShareSourcePost() || post;
  }

  private emitShareCountIncrement(postId: number | undefined, delta: number): void {
    if (!postId || delta <= 0) {
      return;
    }

    this.shareCountIncremented.emit({ postId, delta });
  }
}
